import styled, { CSSObject } from '@emotion/styled'
import { FusionColor } from '../../../theme/FusionStyle'
import { V2L1LoadingIndicator } from '../Indicator/V2L1LoadingIndicator'
import { withProps } from '../../../lib/util'
import { IconProp } from '@fortawesome/fontawesome-svg-core'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

// typescript does not put together enums that use other enums properly
type V2L1ReasonableButtonColor = (
	FusionColor.OrangeMain|
	FusionColor.BlueMain|
	FusionColor.White
)

interface V2L1ReasonableButtonRootProps {
	color?: V2L1ReasonableButtonColor
	loading?: boolean
	hasLabel: boolean
	compact?: boolean
}

export const V2L1ReasonableButtonRoot = styled('button', withProps([],['loading']))<V2L1ReasonableButtonRootProps>(({theme, color = FusionColor.OrangeMain, loading, hasLabel, compact}) => {
	let style: CSSObject = {
		all: 'unset',
		boxSizing: 'border-box',
		position: 'relative',
		'&:hover': {
			// cursor: onClick ? 'pointer' : 'default',
			cursor: 'pointer',
		},
		color: 'inherit',
		display: 'flex',
		flexDirection: 'row',
		justifyContent: 'center',
		alignItems: 'center',
		columnGap: '0.5rem',
		padding: '0.75rem 1.75rem',
		borderRadius: theme.borderRadius,
	}
	// 20250827 jon's latest styling has shorter buttons
	style = {
		...style,
		padding: hasLabel ? '0.5rem 1.5rem' : '0.5rem',
		...theme.fonts.bodyBold,
		fontSize: `${14/16}rem`,
		whiteSpace: 'nowrap',
		// setting minHeight to try for consistent small button sizes...
		// border-box: account for 1px border on top and bottom
		minHeight: 'calc(2rem + 1px + 1px)',
		// needs just a bit more to account for padding
		minWidth: 'calc(2rem + 4px)',
	}
	if(compact) {
		style = {
			...style,
			columnGap: '0.25rem',
			padding: '0.5rem',
		}
	}
	let colorStyle: CSSObject = {}
	let disabledStyle: CSSObject = {
		backgroundColor: theme.colors.thirdDisabled,
		color: loading ? theme.colors.transparent : theme.colors.textDisabled,
		border: 'none',
		'&:hover': {
			cursor: 'default',
		},
	}
	switch(color) {
		case FusionColor.OrangeMain:
			colorStyle = {
				backgroundColor: theme.colors.orangeMain,
				color: theme.colors.white
			}
			break
		case FusionColor.BlueMain:
			colorStyle = {
				backgroundColor: theme.colors.blueMain,
				color: theme.colors.white
			}
			break
		case FusionColor.White:
			colorStyle = {
				backgroundColor: theme.colors.white,
				color: theme.colors.blueMain,
				border: `1px solid ${theme.colors.lines}`,
			}
			break
		// case FusionColor.Transparent:
			// disabledStyle.border = `1px solid ${theme.colors.miniHeader}`
			// break
	}
	return {
		...style,
		...colorStyle,
		'&:disabled': disabledStyle,
	}
})

const LoadingRoot = styled.div(({theme}) => ({
	position: 'absolute',
	width: '100%',
	height: '100%',
	overflow: 'hidden',
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
	'svg': {
		color: theme.colors.text,
	},
}))

interface V2L1ReasonableButtonProps {
	color?: V2L1ReasonableButtonColor
	icon?: IconProp
	label?: string
	compact?: boolean
	loading?: boolean
	disabled?: boolean
	onClick?: React.MouseEventHandler<HTMLButtonElement>
}

export const V2L1ReasonableButton = ({color, icon, label, compact, loading, disabled, onClick}: V2L1ReasonableButtonProps) => {
	return (
		<V2L1ReasonableButtonRoot
			color={color}
			compact={compact}
			loading={loading}
			hasLabel={Boolean(label)}
			disabled={disabled || loading}
			onClick={onClick}
		>
			{icon ? <FontAwesomeIcon icon={icon} fixedWidth /> : null}
			{label}
			{loading ? <LoadingRoot><V2L1LoadingIndicator /></LoadingRoot> : null}
		</V2L1ReasonableButtonRoot>
	)
}
